import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dsrApi, organizationApi } from '../services/api';
import {
  DataSubjectRequestCreate,
  DataSubjectRequestUpdate,
  DataSubjectRequestType,
  DataSubjectRequestChannel
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  Input,
  TextArea,
  Select,
  Option,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  DatePicker
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import HelpTooltip from '../components/HelpTooltip';
import { fieldHelp } from '../helpers/helpTexts';
import { isoToGerman, germanToIso } from '../helpers/dateFormat';

interface DsrWizardPageProps {
  editMode?: boolean;
}

const requestTypeConfig: Record<DataSubjectRequestType, { label: string; description: string }> = {
  [DataSubjectRequestType.ACCESS]: { label: 'Auskunft (Art. 15)', description: 'Auskunft über verarbeitete personenbezogene Daten' },
  [DataSubjectRequestType.RECTIFICATION]: { label: 'Berichtigung (Art. 16)', description: 'Berichtigung unrichtiger personenbezogener Daten' },
  [DataSubjectRequestType.ERASURE]: { label: 'Löschung (Art. 17)', description: 'Löschung personenbezogener Daten (Recht auf Vergessenwerden)' },
  [DataSubjectRequestType.RESTRICTION]: { label: 'Einschränkung (Art. 18)', description: 'Einschränkung der Verarbeitung' },
  [DataSubjectRequestType.PORTABILITY]: { label: 'Datenübertragbarkeit (Art. 20)', description: 'Übertragung der Daten an einen anderen Verantwortlichen' },
  [DataSubjectRequestType.OBJECTION]: { label: 'Widerspruch (Art. 21)', description: 'Widerspruch gegen die Verarbeitung' }
};

const channelConfig: Record<DataSubjectRequestChannel, { label: string }> = {
  [DataSubjectRequestChannel.EMAIL]: { label: 'E-Mail' },
  [DataSubjectRequestChannel.LETTER]: { label: 'Brief' },
  [DataSubjectRequestChannel.PHONE]: { label: 'Telefon' },
  [DataSubjectRequestChannel.IN_PERSON]: { label: 'Persönlich' },
  [DataSubjectRequestChannel.WEB_FORM]: { label: 'Webformular' },
  [DataSubjectRequestChannel.OTHER]: { label: 'Sonstiges' }
};

const steps = [
  { title: 'Grunddaten', icon: 'person-placeholder' },
  { title: 'Anfrage-Details', icon: 'detail-more' }
];

interface FormData {
  organization_id: number | null;
  request_type: DataSubjectRequestType;
  channel: DataSubjectRequestChannel;
  subject_name: string;
  subject_email: string;
  subject_phone: string;
  subject_reference: string;
  received_at: string;
  description: string;
  internal_notes: string;
  assigned_to: string;
}

const defaultFormData: FormData = {
  organization_id: null,
  request_type: DataSubjectRequestType.ACCESS,
  channel: DataSubjectRequestChannel.EMAIL,
  subject_name: '',
  subject_email: '',
  subject_phone: '',
  subject_reference: '',
  received_at: new Date().toISOString().slice(0, 10),
  description: '',
  internal_notes: '',
  assigned_to: ''
};

export default function DsrWizardPage({ editMode = false }: DsrWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    const orgsRes = await organizationApi.getAll();

    if (orgsRes.success && orgsRes.data) {
      if (orgsRes.data.length > 0 && !editMode) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data![0].id }));
      }
    }

    if (editMode && id) {
      const org = orgsRes.data?.[0];
      if (org) {
        const res = await dsrApi.getById(parseInt(id), org.id);
        if (res.success && res.data) {
          const d = res.data;
          setFormData({
            organization_id: d.organization_id,
            request_type: d.request_type,
            channel: d.channel,
            subject_name: d.subject_name,
            subject_email: d.subject_email || '',
            subject_phone: d.subject_phone || '',
            subject_reference: d.subject_reference || '',
            received_at: d.received_at.slice(0, 10),
            description: d.description || '',
            internal_notes: d.internal_notes || '',
            assigned_to: d.assigned_to != null ? String(d.assigned_to) : ''
          });
        } else {
          navigate('/data-subject-requests');
        }
      }
    }

    setInitialLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (editMode && id) {
      const updates: DataSubjectRequestUpdate = {
        request_type: formData.request_type,
        channel: formData.channel,
        subject_name: formData.subject_name,
        subject_email: formData.subject_email || undefined,
        subject_phone: formData.subject_phone || undefined,
        subject_reference: formData.subject_reference || undefined,
        description: formData.description || undefined,
        internal_notes: formData.internal_notes || undefined,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined
      };
      const response = await dsrApi.update(parseInt(id), formData.organization_id!, updates);
      if (response.success) {
        navigate(`/data-subject-requests/${id}`);
      } else {
        setError(response.error || 'Fehler beim Aktualisieren');
      }
    } else {
      const data: DataSubjectRequestCreate = {
        organization_id: formData.organization_id!,
        request_type: formData.request_type,
        channel: formData.channel,
        subject_name: formData.subject_name,
        subject_email: formData.subject_email || undefined,
        subject_phone: formData.subject_phone || undefined,
        subject_reference: formData.subject_reference || undefined,
        received_at: formData.received_at,
        description: formData.description || undefined,
        internal_notes: formData.internal_notes || undefined,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined
      };
      const response = await dsrApi.create(data);
      if (response.success && response.data) {
        navigate(`/data-subject-requests/${response.data.id}`);
      } else {
        setError(response.error || 'Fehler beim Erstellen');
      }
    }

    setLoading(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.subject_name.trim() !== '' && formData.received_at.trim() !== '';
      case 1: return true;
      default: return false;
    }
  };

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout--narrow">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>
            {editMode ? 'Anfrage bearbeiten' : 'Neue Betroffenenanfrage'}
          </Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Anfrage betroffener Personen gemäß Art. 12-22 DSGVO
          </Text>
        </div>
        <Button icon="decline" design="Transparent" onClick={() => navigate('/data-subject-requests')}>
          Abbrechen
        </Button>
      </FlexBox>

      {/* Progress */}
      <FlexBox style={{ gap: '0.5rem', marginBottom: '1.5rem' }}>
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '0.5rem',
              background: i === currentStep
                ? 'var(--sapSelectedColor, #0070f2)'
                : i < currentStep
                  ? 'var(--sapPositiveColor, #107e3e)'
                  : 'var(--sapNeutralBackground, #f5f6f7)',
              color: i <= currentStep ? 'white' : 'var(--sapNeutralTextColor)',
              textAlign: 'center',
              cursor: i < currentStep ? 'pointer' : 'default',
              transition: 'background 0.2s'
            }}
            onClick={() => { if (i < currentStep) setCurrentStep(i); }}
          >
            <Icon name={step.icon} style={{ marginRight: '0.5rem' }} />
            <Text style={{ fontWeight: '600', color: 'inherit', fontSize: '0.8125rem' }}>
              {i + 1}. {step.title}
            </Text>
          </div>
        ))}
      </FlexBox>

      <ProgressIndicator
        value={((currentStep + 1) / steps.length) * 100}
        style={{ marginBottom: '1.5rem' }}
      />

      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {currentStep === 0 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Grunddaten der Anfrage</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Request Type */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label required style={{ fontWeight: '600' }}>Anfragetyp</Label>
                    <HelpTooltip text={fieldHelp.dsr.request_type} inline />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
                    {Object.entries(requestTypeConfig).map(([key, cfg]) => (
                      <div
                        key={key}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '0.375rem',
                          border: formData.request_type === key
                            ? '2px solid var(--sapSelectedColor, #0070f2)'
                            : '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                          background: formData.request_type === key ? 'var(--sapSelectedBackground, #e8f0fe)' : 'transparent',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, request_type: key as DataSubjectRequestType }))}
                      >
                        <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>{cfg.label}</Text>
                        <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.125rem' }}>
                          {cfg.description}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Channel */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Eingangskanal</Label>
                    <HelpTooltip text={fieldHelp.dsr.channel} inline />
                  </div>
                  <Select
                    onChange={(e: any) => setFormData(prev => ({ ...prev, channel: e.detail.selectedOption.value as DataSubjectRequestChannel }))}
                    style={{ width: '100%', maxWidth: '400px' }}
                  >
                    {Object.entries(channelConfig).map(([key, cfg]) => (
                      <Option key={key} value={key} selected={formData.channel === key}>{cfg.label}</Option>
                    ))}
                  </Select>
                </div>

                {/* Subject Name */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Name der betroffenen Person</Label>
                    <HelpTooltip text={fieldHelp.dsr.subject_name} inline />
                  </div>
                  <Input
                    value={formData.subject_name}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, subject_name: e.target.value }))}
                    placeholder="Vor- und Nachname"
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Subject Email */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>E-Mail</Label>
                  <Input
                    value={formData.subject_email}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, subject_email: e.target.value }))}
                    placeholder="E-Mail-Adresse"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>

                {/* Subject Phone */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Telefon</Label>
                  <Input
                    value={formData.subject_phone}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, subject_phone: e.target.value }))}
                    placeholder="Telefonnummer"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>

                {/* Subject Reference */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Referenz (Kundennummer, Personalnummer etc.)</Label>
                    <HelpTooltip text={fieldHelp.dsr.subject_reference} inline />
                  </div>
                  <Input
                    value={formData.subject_reference}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, subject_reference: e.target.value }))}
                    placeholder="z.B. Kunden-Nr. 12345"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>

                {/* Received At */}
                <div>
                  <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Eingangsdatum</Label>
                  <DatePicker
                    formatPattern="dd.MM.yyyy"
                    value={isoToGerman(formData.received_at)}
                    onChange={(e: any) => setFormData(prev => ({ ...prev, received_at: germanToIso(e.detail.value) }))}
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Anfrage-Details</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Description */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Beschreibung der Anfrage</Label>
                  <TextArea
                    value={formData.description}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detaillierte Beschreibung der Anfrage..."
                    rows={4}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Internal Notes */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Interne Notizen</Label>
                  <TextArea
                    value={formData.internal_notes}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                    placeholder="Interne Hinweise zur Bearbeitung..."
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Assigned To */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Zugewiesen an (Benutzer-ID)</Label>
                  <Input
                    value={formData.assigned_to}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                    placeholder="Benutzer-ID (optional)"
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Navigation Buttons */}
      <FlexBox justifyContent="SpaceBetween">
        <Button
          icon="nav-back"
          design="Transparent"
          onClick={() => {
            if (currentStep > 0) setCurrentStep(currentStep - 1);
            else navigate('/data-subject-requests');
          }}
        >
          {currentStep > 0 ? 'Zurück' : 'Abbrechen'}
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button
            icon="navigation-right-arrow"
            iconEnd
            design="Emphasized"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Weiter
          </Button>
        ) : (
          <Button
            icon="save"
            design="Emphasized"
            onClick={handleSubmit}
            disabled={loading || !formData.subject_name.trim() || !formData.received_at.trim()}
          >
            {loading ? 'Wird gespeichert...' : editMode ? 'Aktualisieren' : 'Anfrage erstellen'}
          </Button>
        )}
      </FlexBox>
    </div>
  );
}
